import { useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import MediaDialog from "../atoms/MediaDialog";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import FieldLabel from "../atoms/FieldLabel";

interface ImageInputProps {
  field: string;
  label?: string;
  value: string;
  width?: string | number;
  height?: string | number;
  onChange?: (value: string) => any;
  loading?: boolean;
}

const defaultImage = require("../../assets/no-image.jpg");

const defaultWidth = { xs: 300, md: 500 };
const defaultHeight = { xs: 130, md: 140 };

export default function ImageInput(props: ImageInputProps) {
  const { t } = useTranslation("ImageUploadButton");
  const { value, onChange } = props;
  const [open, setOpen] = useState(false);
  const [filename, setFilename] = useState("");
  const inputRef = useRef<any>(null);

  const handleClickUpload = () => {
    inputRef.current.click();
  };

  const handleClear = () => {
    onChange?.("");
    setFilename("");
  };

  const handleChangeFileSelection = () => {
    for (const file of inputRef.current.files) {
      setFilename(file.name);
      const reader = new FileReader();
      reader.readAsDataURL(file); // Convert file to Base64

      reader.onload = function () {
        const base64String = reader.result?.toString() || ""; // Base64 data URL
        onChange?.(base64String); // Log Base64 string
      };

      reader.onerror = function () {
        console.error("Error reading file");
      };
      break;
    }
    inputRef.current.value = "";
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickImage = () => {
    if (value) setOpen(true);
  };

  return (
    <Stack width="fit-content" spacing={1}>
      <FieldLabel fontWeight={400} fontSize={14}>
        {props.label ?? ""}
      </FieldLabel>

      <Box position="relative">
        {value && (
          <IconButton
            sx={{
              p: 0.25,
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              bgcolor: "background.default",
              ":hover": {
                bgcolor: "background.paper",
              },
            }}
            color="error"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
          >
            <CloseIcon />
          </IconButton>
        )}

        {props.loading ? (
          <Skeleton
            variant="rounded"
            sx={{
              width: props.height ?? defaultWidth,
              height: props.height ?? defaultHeight,
            }}
          />
        ) : value ? (
          <Stack spacing={1}>
            <Box
              onClick={handleClickImage}
              component="img"
              src={value}
              alt="uploaded"
              sx={{
                width: props.height ?? defaultWidth,
                height: props.height ?? defaultHeight,
                objectFit: "contain",
                cursor: "pointer",
                bgcolor: "#fafafa",
                borderRadius: (theme) =>
                  (theme.shape.borderRadius as number) / 8,
              }}
            />
            <Button
              variant="outlined"
              startIcon={<ImageIcon />}
              onClick={handleClickUpload}
            >
              {t("Change")}
            </Button>
          </Stack>
        ) : (
          <Box
            onClick={handleClickUpload}
            sx={{
              width: defaultWidth,
              height: props.height ?? defaultHeight,
              border: "2px dashed #ccc",
              borderRadius: 2,
              bgcolor: "#fafafa",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              cursor: "pointer",
            }}
          >
            <ImageIcon sx={{ fontSize: 40, color: "#888" }} />
            <Typography fontSize={14} mt={1}>
              {t("ClickToUpload")}
            </Typography>
            <Typography fontSize={12} color="text.secondary">
              {t("ImgSupported")}
            </Typography>
          </Box>
        )}
      </Box>

      <input
        ref={inputRef}
        name={props.field}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleChangeFileSelection}
      />

      <MediaDialog
        filename={filename}
        open={open}
        src={value}
        type="image"
        onClose={handleClose}
      />
    </Stack>
  );
}
